import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const LogsList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [filters, setFilters] = useState({
    projeto: '',
    dateFrom: '',
    dateTo: '',
    tipo: '',
    search: ''
  });
  const [projetos, setProjetos] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchProjetos();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/logs');
      setLogs(response.data.data.logs);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjetos = async () => {
    try {
      const response = await api.get('/api/projetos');
      setProjetos(response.data.data.projetos);
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/logs/${id}`);
      toast.success('Log excluído com sucesso!');
      fetchLogs();
    } catch (error) {
      console.error('Erro ao excluir log:', error);
      toast.error('Erro ao excluir log');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      projeto: '',
      dateFrom: '',
      dateTo: '',
      tipo: '',
      search: ''
    });
  };

  const getLogTypeColor = (atualizacoes) => {
    if (!atualizacoes || atualizacoes.length === 0) return '#6b7280';
    const tipos = atualizacoes.map(a => a.tipo);
    if (tipos.includes('codigo')) return '#10b981';
    if (tipos.includes('link')) return '#3b82f6';
    if (tipos.includes('imagem')) return '#8b5cf6';
    return '#f59e0b';
  };

  const getLogTypeIcon = (atualizacoes) => {
    if (!atualizacoes || atualizacoes.length === 0) return '📝';
    const tipos = atualizacoes.map(a => a.tipo);
    if (tipos.includes('codigo')) return '💻';
    if (tipos.includes('link')) return '🔗';
    if (tipos.includes('imagem')) return '🖼️';
    return '📄';
  };

  // Formatar data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar logs
  const filteredLogs = logs.filter(log => {
    const matchesProjeto = !filters.projeto || log.projeto === filters.projeto;
    const matchesSearch = !filters.search || 
      log.projeto.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.atualizacoes.some(a => a.texto.toLowerCase().includes(filters.search.toLowerCase()));
    
    let matchesDate = true;
    if (filters.dateFrom || filters.dateTo) {
      const logDate = new Date(log.dataCriacao);
      if (filters.dateFrom) {
        matchesDate = matchesDate && logDate >= new Date(filters.dateFrom);
      }
      if (filters.dateTo) {
        matchesDate = matchesDate && logDate <= new Date(filters.dateTo);
      }
    }
    
    const matchesTipo = !filters.tipo || 
      log.atualizacoes.some(a => a.tipo === filters.tipo);
    
    return matchesProjeto && matchesSearch && matchesDate && matchesTipo;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" style={{borderTopColor: '#0066FF'}}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with glassmorphism */}
      <div style={{
        background: 'rgba(30, 30, 30, 0.25)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
      }} className="rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-white font-display">Timeline de Logs</h1>
          <Link
            to="/admin/logs/novo"
            style={{background: 'linear-gradient(90deg, #0066FF, #6E44FF)'}}
            className="px-6 py-3 text-white rounded-xl hover:opacity-90 transition-all duration-300 font-medium shadow-lg"
          >
            ✨ Novo Log
          </Link>
        </div>
        
        {/* Filtros Premium */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="🔍 Buscar logs..."
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
            />
          </div>
          
          <select
            name="projeto"
            value={filters.projeto}
            onChange={handleFilterChange}
            className="px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
          >
            <option value="">📁 Todos os projetos</option>
            {projetos.map((projeto) => (
              <option key={projeto._id} value={projeto.nome}>
                {projeto.nome}
              </option>
            ))}
          </select>
          
          <input
            type="date"
            name="dateFrom"
            value={filters.dateFrom}
            onChange={handleFilterChange}
            className="px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
          />
          
          <input
            type="date"
            name="dateTo"
            value={filters.dateTo}
            onChange={handleFilterChange}
            className="px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
          />
          
          <button
            onClick={clearFilters}
            className="px-4 py-3 bg-gray-700/50 text-gray-300 rounded-xl hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm"
          >
            🗑️ Limpar
          </button>
        </div>
      </div>

      {/* Timeline Container */}
      {filteredLogs.length > 0 ? (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5" style={{background: 'rgba(0, 102, 255, 0.3)'}}></div>
          
          <div className="space-y-8">
            {filteredLogs
              .sort((a, b) => new Date(b.dataCriacao) - new Date(a.dataCriacao))
              .map((log, index) => (
              <div key={log._id} className="relative pl-20">
                {/* Timeline Indicator */}
                <div 
                  className="absolute left-6 w-4 h-4 rounded-full border-4 border-gray-900 shadow-lg transform -translate-x-1/2"
                  style={{
                    backgroundColor: getLogTypeColor(log.atualizacoes),
                    boxShadow: `0 0 0 4px rgba(0, 102, 255, 0.1), 0 0 20px ${getLogTypeColor(log.atualizacoes)}40`
                  }}
                ></div>
                
                {/* Log Card with Glassmorphism */}
                <div 
                  style={{
                    background: 'rgba(30, 30, 30, 0.25)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                  }}
                  className="rounded-2xl p-6 hover:scale-[1.02] transition-all duration-300 group cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getLogTypeIcon(log.atualizacoes)}</span>
                      <div>
                        <h3 className="text-xl font-bold text-white font-display">{log.projeto}</h3>
                        <p className="text-gray-400 text-sm">{formatDateTime(log.dataCriacao)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {/* Status Badge */}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        log.ativo 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {log.ativo ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Link
                          to={`/admin/logs/editar/${log._id}`}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all duration-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(log._id);
                          }}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-300"
                        >
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Updates Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 font-medium">
                        📋 {log.atualizacoes.length} atualizações
                      </span>
                      <span className="text-blue-400 text-sm">
                        {expandedLog === log._id ? '▼ Minimizar' : '▶ Expandir'}
                      </span>
                    </div>
                    
                    {/* Collapsed Preview */}
                    {expandedLog !== log._id && log.atualizacoes.length > 0 && (
                      <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
                        <p className="text-gray-300 text-sm line-clamp-2">
                          {log.atualizacoes[0].texto}
                        </p>
                        {log.atualizacoes.length > 1 && (
                          <p className="text-gray-500 text-xs mt-1">
                            +{log.atualizacoes.length - 1} mais atualizações...
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Expanded Content */}
                    {expandedLog === log._id && (
                      <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                        {log.atualizacoes.map((atualizacao, idx) => (
                          <div key={idx} className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                            <div className="flex items-start justify-between mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                atualizacao.tipo === 'codigo' ? 'bg-green-500/20 text-green-400' :
                                atualizacao.tipo === 'link' ? 'bg-blue-500/20 text-blue-400' :
                                atualizacao.tipo === 'imagem' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {atualizacao.tipo === 'codigo' ? '💻 Código' :
                                 atualizacao.tipo === 'link' ? '🔗 Link' :
                                 atualizacao.tipo === 'imagem' ? '🖼️ Imagem' :
                                 '📝 Texto'}
                              </span>
                            </div>
                            <p className="text-gray-200">{atualizacao.texto}</p>
                            {atualizacao.link && (
                              <a 
                                href={atualizacao.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-block mt-2 text-blue-400 hover:text-blue-300 text-sm"
                              >
                                🔗 {atualizacao.link}
                              </a>
                            )}
                          </div>
                        ))}
                        
                        {/* Tags */}
                        {log.tags && log.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {log.tags.map((tag, idx) => (
                              <span key={idx} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs border border-blue-500/30">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div 
          style={{
            background: 'rgba(30, 30, 30, 0.25)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }}
          className="rounded-2xl p-12 text-center"
        >
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-xl font-bold text-white mb-2 font-display">Nenhum log encontrado</h3>
          <p className="text-gray-400 mb-6">Comece criando seu primeiro log de desenvolvimento</p>
          <Link
            to="/admin/logs/novo"
            style={{background: 'linear-gradient(90deg, #0066FF, #6E44FF)'}}
            className="inline-block px-6 py-3 text-white rounded-xl hover:opacity-90 transition-all duration-300 font-medium shadow-lg"
          >
            ✨ Criar Primeiro Log
          </Link>
        </div>
      )}

      {/* Modal de confirmação de exclusão com glassmorphism */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div 
            style={{
              background: 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px 0 rgba(0, 0, 0, 0.5)'
            }}
            className="rounded-2xl p-8 max-w-md w-full mx-4 animate-in zoom-in-95 duration-300"
          >
            <div className="text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-white mb-2 font-display">Confirmar Exclusão</h3>
              <p className="text-gray-300 mb-8">
                Tem certeza que deseja excluir este log? Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-6 py-3 bg-gray-700/50 text-gray-300 rounded-xl hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete)}
                  className="px-6 py-3 bg-red-600/80 text-white rounded-xl hover:bg-red-700/80 transition-all duration-300 backdrop-blur-sm"
                >
                  🗑️ Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsList;
